/**
 * DpiLivePage — viewer DPI « live » (US-VDPI-06).
 *
 * Montre EN DIRECT les saisies vocales se remplir (démo écran projeté).
 * - Données : GET /api/v1/dpi (merge référence pmsi + saisies + journal),
 *   rafraîchies par polling ~1s (useDpiPolling, UX-2=A — pas de WebSocket).
 * - Vue clinique « dossier » par défaut : carte patient + sections
 *   constantes / administration / transmission / refus / score / surveillance
 *   qui se remplissent au fil des saisies (UX-3=C).
 * - Flux journal temps réel (timeline) + toggle discret « JSON brut ».
 * - Animation slide-in/highlight sur toute nouvelle saisie / ligne de journal.
 *
 * SaisieRow / JournalPanel / useNewItems / summarizeData sont mutualisés dans
 * `@/shared/components/dpi` (Phase 1 A3, DRY — partagés avec /vocal).
 */

import { useMemo, useState } from 'react';
import { useDpiPolling } from '@/features/admin/hooks/useDpiPolling';
import { JournalPanel, SaisieRow, useNewItems } from '@/shared/components/dpi';
import type { DpiSection, SaisieOut } from '@/shared/types/dpi';
import type { Patient, Soignant } from '@/shared/types/pmsi';
import './dpi-live.css';

// Ordre + libellés des sections cliniques affichées dans le dossier.
const SECTIONS: { key: DpiSection; label: string; icon: string }[] = [
  { key: 'constantes',    label: 'Constantes',     icon: 'bi-heart-pulse' },
  { key: 'administration', label: 'Administrations', icon: 'bi-capsule' },
  { key: 'transmission',  label: 'Transmissions',  icon: 'bi-chat-left-text' },
  { key: 'refus',         label: 'Refus',          icon: 'bi-x-octagon' },
  { key: 'score',         label: 'Scores',         icon: 'bi-clipboard-data' },
  { key: 'surveillance',  label: 'Surveillances',  icon: 'bi-eye' },
];

export function DpiLivePage() {
  const { data, loading, error } = useDpiPolling();
  const [showJson, setShowJson] = useState(false);

  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="text-muted">Connexion au DPI live...</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="alert alert-danger d-flex align-items-center gap-2">
        <i className="bi bi-exclamation-triangle-fill fs-5" />
        <span>Erreur : {error}</span>
      </div>
    );
  }

  if (!data) return null;

  const { service } = data.reference;
  const soignants = service.soignants;
  const patients = service.chambres.flatMap((c) => c.patients);

  return (
    <div className="container-fluid">
      {/* En-tête live + toggle JSON */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="rounded-circle bg-primary bg-opacity-10 p-3">
          <i className="bi bi-activity fs-4 text-primary" />
        </div>
        <div>
          <h2 className="mb-0 fw-bold">DPI live</h2>
          <span className="text-muted small d-flex align-items-center gap-2">
            <span className="dpi-live-dot" /> Synchronisation temps réel · {service.nom}
          </span>
        </div>
        <div className="ms-auto d-flex align-items-center gap-3">
          <span className="badge bg-success bg-opacity-75 fs-6">
            <i className="bi bi-pencil-square me-1" />
            {data.saisies.length} saisie(s)
          </span>
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="dpi-json-toggle"
              checked={showJson}
              onChange={(e) => setShowJson(e.target.checked)}
            />
            <label className="form-check-label small text-muted" htmlFor="dpi-json-toggle">
              JSON brut
            </label>
          </div>
        </div>
      </div>

      {showJson ? (
        <JsonRawView data={data} />
      ) : (
        <div className="row g-3">
          {/* Colonne dossiers patients */}
          <div className="col-12 col-xl-8">
            <div className="row g-3">
              {patients.map((patient) => (
                <DossierCard
                  key={patient.id}
                  patient={patient}
                  saisies={data.saisies.filter((s) => s.patient_id === patient.id)}
                  soignants={soignants}
                />
              ))}
            </div>
          </div>
          {/* Colonne journal temps réel */}
          <div className="col-12 col-xl-4">
            <JournalPanel journal={data.journal} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dossier patient (carte + sections cliniques) ────────────────────────────

function DossierCard({
  patient,
  saisies,
  soignants,
}: {
  patient: Patient;
  saisies: SaisieOut[];
  soignants: Soignant[];
}) {
  const newIds = useNewItems(saisies.map((s) => s.id));
  const prescripteur = soignants.find(
    (s) => s.id === patient.sejour.ordonnance.prescripteur_id,
  );

  return (
    <div className="col-12 col-md-6">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center gap-2">
          <div
            className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center"
            style={{ width: 36, height: 36 }}
          >
            <i className="bi bi-person text-primary" />
          </div>
          <div>
            <div className="fw-semibold">{patient.prenom} {patient.nom}</div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              {patient.id}
              {prescripteur && <span className="ms-2">· Dr {prescripteur.nom}</span>}
            </div>
          </div>
          <span className="badge bg-secondary bg-opacity-75 ms-auto">
            {saisies.length} saisie(s)
          </span>
        </div>
        <div className="card-body p-3">
          {SECTIONS.map((section) => {
            const sectionSaisies = saisies.filter((s) => s.section === section.key);
            return (
              <div key={section.key} className="mb-3">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i className={`bi ${section.icon} text-primary`} style={{ fontSize: '0.9rem' }} />
                  <span className="fw-semibold small">{section.label}</span>
                  {sectionSaisies.length > 0 && (
                    <span className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: '0.7rem' }}>
                      {sectionSaisies.length}
                    </span>
                  )}
                </div>
                {sectionSaisies.length === 0 ? (
                  <div className="text-muted ms-4" style={{ fontSize: '0.78rem' }}>
                    —
                  </div>
                ) : (
                  sectionSaisies.map((s) => (
                    <SaisieRow key={s.id} saisie={s} isNew={newIds.has(s.id)} />
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Vue JSON brut colorisée ─────────────────────────────────────────────────

function JsonRawView({ data }: { data: unknown }) {
  const html = useMemo(() => colorizeJson(data), [data]);
  return (
    <pre
      className="dpi-json-raw"
      data-testid="dpi-json-raw"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Colorisation JSON simple par regex (rendu lisible, pas de dépendance). */
function colorizeJson(data: unknown): string {
  const json = escapeHtml(JSON.stringify(data, null, 2));
  return json.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls = 'dpi-json-number';
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? 'dpi-json-key' : 'dpi-json-string';
      } else if (/true|false/.test(match)) {
        cls = 'dpi-json-bool';
      } else if (/null/.test(match)) {
        cls = 'dpi-json-null';
      }
      return `<span class="${cls}">${match}</span>`;
    },
  );
}
